import {
  BOT_ACTION_LABELS,
  BOT_INTENT_LABELS,
  BOT_PLAN_LABELS,
  BOT_PLANS,
  describeExtracted,
  type BotPlan,
  type BotRunDto,
  type BotSessionDto,
} from '@performance-crm/shared';
import { Bot, Loader2, Send, User } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast-context';
import { useAdminProjects, useCreateBotSession, useSendBotMessage } from '@/lib/queries';

const money = (usd: number) => (usd === 0 ? 'USD 0' : `USD ${usd.toFixed(4)}`);
const TEMP: Record<string, string> = { FRIO: 'frío', TIBIO: 'tibio', CALIENTE: 'caliente' };

/** Probar el bot: el administrador conversa como si fuera el cliente y ve qué entendió, qué decidió y cuánto costó. */
export function BotSimulator({
  companyId,
  defaultPlan,
}: {
  companyId: string;
  defaultPlan: BotPlan;
}) {
  const { data: projects } = useAdminProjects(companyId);
  const create = useCreateBotSession();
  const send = useSendBotMessage();
  const toast = useToast();
  const [projectId, setProjectId] = useState('');
  const [plan, setPlan] = useState<BotPlan>(defaultPlan);
  const [session, setSession] = useState<BotSessionDto | null>(null);
  const [selectedRun, setSelectedRun] = useState<BotRunDto | null>(null);
  const [text, setText] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const activeProjects = (projects ?? []).filter((p) => p.active);
  useEffect(() => {
    if (!projectId && activeProjects[0]) setProjectId(activeProjects[0].id);
  }, [activeProjects, projectId]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [session?.messages.length]);

  const start = async () => {
    try {
      const r = await create.mutateAsync({ companyId, projectId, plan });
      setSession(r.session);
      setSelectedRun(r.run);
    } catch (err) {
      toast.error('No se pudo iniciar la conversación', (err as Error).message);
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!session || !text.trim()) return;
    const msg = text.trim();
    setText('');
    try {
      const r = await send.mutateAsync({ sessionId: session.id, text: msg });
      setSession(r.session);
      setSelectedRun(r.run);
    } catch (err) {
      toast.error('El bot no pudo responder', (err as Error).message);
    }
  };
  const runOfMessage = (index: number) => {
    // Cada respuesta del bot corresponde a un turno con respuesta, en orden.
    const botIndex =
      session!.messages.slice(0, index + 1).filter((m) => m.role === 'bot').length - 1;
    return session!.runs.filter((r) => r.replyText)[botIndex] ?? null;
  };

  return (
    <Card data-testid="bot-simulator">
      <CardHeader>
        <CardTitle>Probar el bot</CardTitle>
        <CardDescription>
          Conversa como si fueras el cliente. No se envía nada por WhatsApp ni se crean leads; cada
          respuesta muestra qué entendió el bot, qué decidió y cuánto costó.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[200px] flex-1 space-y-1">
            <Label htmlFor="sim-project">Proyecto</Label>
            <Select
              id="sim-project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              {activeProjects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="min-w-[180px] space-y-1">
            <Label htmlFor="sim-plan">Plan</Label>
            <Select id="sim-plan" value={plan} onChange={(e) => setPlan(e.target.value as BotPlan)}>
              {BOT_PLANS.map((p) => (
                <option key={p} value={p}>
                  {BOT_PLAN_LABELS[p]}
                </option>
              ))}
            </Select>
          </div>
          <Button
            type="button"
            onClick={() => void start()}
            disabled={!projectId || create.isPending}
            data-testid="bot-sim-new"
          >
            {create.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Bot className="h-4 w-4" />
            )}{' '}
            {session ? 'Nueva conversación' : 'Empezar conversación'}
          </Button>
        </div>
        {session && (
          <div className="grid gap-3 lg:grid-cols-[3fr_2fr]">
            <div className="flex h-[420px] flex-col rounded-md border">
              <div className="flex-1 space-y-2 overflow-y-auto p-3" data-testid="bot-sim-chat">
                {session.messages.map((m, i) => (
                  <button
                    key={`${m.at}-${i}`}
                    type="button"
                    onClick={() => m.role === 'bot' && setSelectedRun(runOfMessage(i))}
                    className={`flex max-w-[85%] items-start gap-2 rounded-lg px-3 py-2 text-left text-sm ${
                      m.role === 'bot'
                        ? 'bg-muted mr-auto hover:ring-1'
                        : 'bg-primary text-primary-foreground ml-auto cursor-default'
                    }`}
                    data-testid="bot-sim-message"
                    data-role={m.role}
                  >
                    {m.role === 'bot' ? (
                      <Bot className="mt-0.5 h-4 w-4 shrink-0" />
                    ) : (
                      <User className="mt-0.5 h-4 w-4 shrink-0" />
                    )}
                    <span className="whitespace-pre-wrap">{m.text}</span>
                  </button>
                ))}
                <div ref={endRef} />
              </div>
              <form onSubmit={(e) => void submit(e)} className="flex gap-2 border-t p-2">
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Escribe como el cliente…"
                  aria-label="Mensaje del cliente"
                  data-testid="bot-sim-input"
                  maxLength={1000}
                />
                <Button
                  type="submit"
                  disabled={!text.trim() || send.isPending}
                  data-testid="bot-sim-send"
                >
                  {send.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </form>
            </div>
            <div className="space-y-2 rounded-md border p-3 text-sm" data-testid="bot-sim-trace">
              <p className="font-medium">Qué pasó en este turno</p>
              {selectedRun ? (
                <RunTrace run={selectedRun} />
              ) : (
                <p className="text-muted-foreground text-xs">
                  Toca una respuesta del bot para ver su traza.
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function RunTrace({ run }: { run: BotRunDto }) {
  const u = run.understanding;
  const d = run.decision;
  const model = (m: BotRunDto['models']['reply']) =>
    !m
      ? 'reglas y plantillas'
      : `${m.provider}:${m.model}${m.fallbackToFake ? ' (simulado: sin clave del proveedor)' : ''}`;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
      {u && (
        <>
          <dt className="text-muted-foreground">Entendió</dt>
          <dd>
            {BOT_INTENT_LABELS[u.intent]} ·{' '}
            <Badge
              tone={
                u.temperature === 'CALIENTE'
                  ? 'danger'
                  : u.temperature === 'TIBIO'
                    ? 'warning'
                    : 'muted'
              }
            >
              {TEMP[u.temperature]}
            </Badge>
            {describeExtracted(u.data) ? (
              <span className="block">{describeExtracted(u.data)}</span>
            ) : null}
          </dd>
        </>
      )}
      {d && (
        <>
          <dt className="text-muted-foreground">Decidió</dt>
          <dd>
            {BOT_ACTION_LABELS[d.action]}
            {d.priority && (
              <Badge tone="danger" className="ml-1">
                prioridad
              </Badge>
            )}
            <span className="text-muted-foreground block">{d.reason}</span>
          </dd>
        </>
      )}
      <dt className="text-muted-foreground">Modelos</dt>
      <dd>
        entender: {model(run.models.understand)}
        <br />
        redactar: {model(run.models.reply)}
      </dd>
      <dt className="text-muted-foreground">Costo</dt>
      <dd>
        {money(run.costUsd)} · {run.latencyMs} ms ·{' '}
        {Object.values(run.usage).reduce(
          (s, x) => s + (x?.input ?? 0) + (x?.cachedInput ?? 0) + (x?.output ?? 0),
          0,
        )}{' '}
        tokens
      </dd>
      <dt className="text-muted-foreground">Verificación</dt>
      <dd>
        {run.verified ? <Badge tone="success">pasó</Badge> : <Badge tone="danger">no pasó</Badge>}
        {run.issues.length > 0 && (
          <ul className="mt-1 list-disc pl-4">
            {run.issues.map((i, n) => (
              <li key={n}>{i}</li>
            ))}
          </ul>
        )}
      </dd>
    </dl>
  );
}
